import { getApiCountry } from '/api/sdk.gen';
import type { GetApiCountryResponse } from '/api/types.gen';
import type { RootState } from '/business/shared/state/store';
import { toNormaliseErrorInput, type NormaliseErrorInput } from '/shared/functions/normaliseError';

type GetCountriesStartedAction = {
  type: 'GET_COUNTRIES_STARTED';
  timestamp: number;
};
type GetCountriesFulfilledAction = {
  type: 'GET_COUNTRIES_FULFILLED';
  countries: GetApiCountryResponse;
};
type GetCountriesRejectedAction = {
  type: 'GET_COUNTRIES_REJECTED';
  error: {
    context: 'LOCATION';
    message: string;
  };
};
type GetCountriesAction = GetCountriesStartedAction | GetCountriesFulfilledAction | GetCountriesRejectedAction;
type SharedDispatch = (action: GetCountriesAction) => GetCountriesAction;
type SharedGetState = () => Pick<RootState, 'location'>;

interface GetCountriesOptions {
  useB2BMarkets?: boolean;
}

function getErrorMessage(error: NormaliseErrorInput): string {
  return error instanceof Error ? error.message : 'Could not get countries';
}

const getCountries =
  ({ useB2BMarkets = false }: GetCountriesOptions) =>
  async (
    dispatch: SharedDispatch,
    getState: SharedGetState,
  ): Promise<RootState['location']['countries'] | undefined> => {
    try {
      const { location: locationState } = getState?.() || {};

      if (locationState?.fetching) {
        return locationState?.countries || null;
      }

      dispatch({ type: 'GET_COUNTRIES_STARTED', timestamp: Date.now() });
      const { data: countries } = await getApiCountry({
        query: {
          useB2BMarkets,
        },
      });
      if (!countries || !Array.isArray(countries) || countries.length <= 0) {
        throw new Error('Could not get countries');
      }
      dispatch({ type: 'GET_COUNTRIES_FULFILLED', countries });
    } catch (error) {
      dispatch({
        type: 'GET_COUNTRIES_REJECTED',
        error: { message: getErrorMessage(toNormaliseErrorInput(error)), context: 'LOCATION' },
      });
    }
  };

export default getCountries;
